/**
 * aiService.ts
 *
 * Dual-mode AI service wrapper.
 *
 *  - prototype  : context-aware mock responses (references student name, subject, path)
 *  - production : POST /api/ai/chat — backend proxies to Anthropic (API key stays server-side)
 *
 * Consumers use the same interface regardless of mode.
 */

import config from '../../../config';
import { AuthStore } from '@/hooks/stores/AuthStore';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type AIProvider = 'anthropic' | 'openai' | 'gemini'

export type AISource = 'companion' | 'widget' | 'quiz' | 'context'

export interface AIChatParams {
  messages: ChatMessage[];
  systemPrompt: string;
  model?: 'haiku' | 'sonnet';
  /** Optional provider override — backend resolves from school config / global env if omitted */
  provider?: AIProvider;
  /** Tracks which part of the app triggered the call (for analytics) */
  source?: AISource;
}

// ─── Prototype mock templates ─────────────────────────────────────────────────

type MockCategory =
  | 'career_advice'
  | 'subject_help'
  | 'study_tips'
  | 'path_info'
  | 'encouragement'
  | 'quiz_help'
  | 'maths'
  | 'science'
  | 'fallback'

const TEMPLATES: Record<MockCategory, string[]> = {
  career_advice: [
    "Based on your interests and what I can see of your strengths, you're building a great foundation. The path you're on opens doors to so many exciting roles — keep exploring and don't be afraid to ask questions.",
    "Career journeys are rarely straight lines. The subjects you're studying right now, especially the ones that challenge you, are exactly what builds the thinking skills every career needs.",
    "Your curiosity is one of your greatest assets. Professionals in every field started exactly where you are — curious and willing to learn.",
  ],
  subject_help: [
    "Let's break this down step by step. The key is to understand *why* the rule works, not just memorise it. Once you get the 'why', the rest clicks into place.",
    "This topic trips up a lot of students at first — you're definitely not alone! The trick is to connect it to something you already know well.",
    "Great question. The concept here builds on what you've already learned, so let's start from that foundation and work forward.",
  ],
  study_tips: [
    "Try the Pomodoro method — 25 minutes of focused study, then a 5-minute break. Your brain consolidates information during those short rests.",
    "Active recall beats rereading every time. Close your notes, then try to write down everything you remember. The act of retrieval is what locks it in.",
    "Space your revision across multiple days rather than one marathon session. Your future self will thank you when the exam arrives.",
  ],
  path_info: [
    "The career path you're exploring is a broad and exciting field. The good news is the skills you're building now — critical thinking, problem solving, communication — are valued across every specialisation in it.",
    "Students who explore this area often find that the real-world applications are far more interesting than the textbook version. Try to connect what you're learning to news, projects, and real problems.",
    "This field is evolving fast, which means there's more opportunity than ever for people who get in early and build solid foundations.",
  ],
  encouragement: [
    "Progress isn't always visible in the moment — but every lesson you complete, every question you ask, is building something real. Keep going.",
    "You're doing better than you think. The fact that you're here, asking questions and engaging with your learning, already puts you ahead.",
    "Challenges are where growth happens. The fact that this feels hard means you're working at the right level.",
  ],
  quiz_help: [
    "For multiple-choice questions, always eliminate the obviously wrong answers first. That alone improves your odds dramatically.",
    "Read the question twice before looking at the options. Often the question itself contains clues about the right answer.",
    "When you're unsure, trust your first instinct on factual questions. For calculation questions, always show your working — even in your head.",
  ],
  maths: [
    "Mathematics is really about patterns. Once you spot the pattern in a problem type, you can solve every version of it. Let's find the pattern here.",
    "The formula is just a shortcut for something logical. Understanding where the formula comes from makes it much easier to remember and apply.",
    "Show your working — always. It helps you catch errors, and it helps me (or your teacher) see exactly where to help you.",
  ],
  science: [
    "Science is built on 'why' questions. The best scientists are the ones who never stop asking why. Your question shows you're thinking like a scientist.",
    "Connecting concepts across topics is where science gets really powerful. Once you see how chemistry, biology, and physics overlap, everything makes more sense.",
    "The lab and the theory support each other. If you're finding theory dry, try thinking about the experiment that first proved it — there's always a great story there.",
  ],
  fallback: [
    "That's a great thing to think about. Let me help you work through it. What specifically are you finding tricky?",
    "I'm here to help. Could you tell me a bit more about what you're working on? The more specific you are, the better I can support you.",
    "Good question! Let's explore this together. What's your current understanding so far?",
  ],
}

function pickTemplate(category: MockCategory, seed: string): string {
  const arr = TEMPLATES[category]
  // Deterministic pick based on message content so responses feel varied but stable
  const idx = seed.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % arr.length
  return arr[idx]
}

function extractContext(systemPrompt: string): { name: string; subject: string; path: string } {
  const nameMatch = systemPrompt.match(/AI companion for ([^,]+),/)
  const pathMatch = systemPrompt.match(/exploring: ([^.]+)\./)
  return {
    name: nameMatch?.[1]?.trim() ?? 'there',
    subject: '',
    path: pathMatch?.[1]?.trim() ?? 'your chosen path',
  }
}

function classifyMessage(text: string): MockCategory {
  const lower = text.toLowerCase()
  if (/career|job|profession|future|work/.test(lower)) return 'career_advice'
  if (/math|algebra|calculus|geometry|equation|number|formula/.test(lower)) return 'maths'
  if (/physics|chemistry|biology|science/.test(lower)) return 'science'
  if (/study|revise|revision|prepare|exam|test|tips/.test(lower)) return 'study_tips'
  if (/quiz|question|multiple.choice|answer/.test(lower)) return 'quiz_help'
  if (/path|curriculum|module|lesson|course/.test(lower)) return 'path_info'
  if (/help|explain|understand|confused|stuck|don.t get|how do/.test(lower)) return 'subject_help'
  if (/motivat|encour|feel|hard|difficult|give up|frustrated/.test(lower)) return 'encouragement'
  return 'fallback'
}

function buildMockResponse(params: AIChatParams): string {
  const lastUserMsg = [...params.messages].reverse().find((m) => m.role === 'user')?.content ?? ''
  const ctx = extractContext(params.systemPrompt)
  const category = classifyMessage(lastUserMsg)
  const template = pickTemplate(category, lastUserMsg)

  // Personalise with student name where it flows naturally
  const personalised = template
    .replace(/^(Let's|Try|For|Great|Show|Mathematics|Science|That|Good|I'm|Challenges|Progress|Career|Students|This|You're|Based|Your)/, (m) => {
      // Only prefix the first sentence with name on some categories for natural feel
      if (['encouragement', 'career_advice'].includes(category)) {
        return `${ctx.name}, ${m.toLowerCase()}`
      }
      return m
    })

  return personalised
}

// ─── Production call ──────────────────────────────────────────────────────────

async function chatProduction(params: AIChatParams): Promise<string> {
  // Read JWT from AuthStore — backend validates it before forwarding to AI provider
  const token = (AuthStore.getState().currentUser as any)?.accessToken ?? ''

  const res = await fetch(`${config.apiBaseUrl}/api/v1/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      messages: params.messages,
      systemPrompt: params.systemPrompt,
      model: params.model ?? 'haiku',
      ...(params.provider ? { provider: params.provider } : {}),
      ...(params.source   ? { source: params.source }     : {}),
    }),
  })
  if (!res.ok) throw new Error(`AI service error: ${res.status}`)
  const data = await res.json()
  // Backend returns { success: true, data: { content, model, provider } }
  return (data.data?.content ?? data.content) as string
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const aiService = {
  async chat(params: AIChatParams): Promise<string> {
    if (config.appMode === 'prototype') {
      // Simulate short network delay for realistic UX
      await new Promise((r) => setTimeout(r, 600 + Math.random() * 800))
      return buildMockResponse(params)
    }
    return chatProduction(params)
  },

  /** Convenience: single-turn question with student context baked in */
  async ask(question: string, systemPrompt: string, model?: 'haiku' | 'sonnet'): Promise<string> {
    return this.chat({
      messages: [{ role: 'user', content: question }],
      systemPrompt,
      model,
    })
  },
}
